import { loadLayoutSession } from '@verclam/docx-kit-layout-engine/session';
import sessionWasmUrl from '@verclam/docx-kit-layout-engine/session/session.wasm?url';
import { installExpressionsRust } from '@verclam/docx-kit-expressions/rust';
import ooxmlWasmUrl from '@verclam/docx-kit-ooxml-io/leaves-template.wasm?url';
import { loadTagKit } from '@verclam/docx-kit-tag-contract/rust';
import tagKitWasmUrl from '@verclam/docx-kit-tag-contract/tag-kit.wasm?url';
import { loadTemplateWasm } from '@verclam/docx-kit-template-engine';

const WASM_CONTENT_TYPE = 'application/wasm';

/** Streaming compilation needs the `application/wasm` content type; any other server answer falls back to a buffered compile. */
const compileWasm = async (url: string): Promise<WebAssembly.Module> => {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`${url} : HTTP ${response.status}`);
	}

	if (response.headers.get('content-type')?.startsWith(WASM_CONTENT_TYPE) === true) {
		return WebAssembly.compileStreaming(response);
	}

	return WebAssembly.compile(await response.arrayBuffer());
};

const loadTemplateEngine = async (): Promise<void> => {
	await loadTemplateWasm(await compileWasm(ooxmlWasmUrl));
};

const loadExpressions = async (): Promise<void> => {
	installExpressionsRust(await loadTagKit(await compileWasm(tagKitWasmUrl)));
};

const loadLayout = async (): Promise<void> => {
	await loadLayoutSession(await compileWasm(sessionWasmUrl));
};

let pending: Promise<void> | undefined;

/** Loads the three docx-kit WebAssembly modules once per session; a rejected attempt is forgotten so the next call retries. */
const loadDocxKitModules = (): Promise<void> => {
	if (pending === undefined) {
		const attempt = Promise.all([loadTemplateEngine(), loadExpressions(), loadLayout()]).then(
			() => undefined,
		);
		pending = attempt;
		attempt.catch(() => {
			if (pending === attempt) {
				pending = undefined;
			}
		});
	}

	return pending;
};

export default loadDocxKitModules;
